const en = {
    common: {
        loading: 'Loading',
        processing: 'Processing',
        save: 'Save',
        cancel: 'Cancel',
        close: 'Close',
        back: 'Back',
        ok: 'OK',
        online: 'Online',
        offline: 'Offline',
        sendMessage: 'Send message'
    },
    presence: {
        label: 'Status',
        online: 'Online',
        idle: 'Idle',
        dnd: 'Do not disturb',
        invisible: 'Invisible',
        offline: 'Offline',
        hint: {
            online: 'Your friends see you online.',
            idle: "You show as idle. You also go idle by yourself after a while without activity.",
            dnd: 'Notification pop-ups and sounds are off; notifications still collect in the list.',
            invisible: "Everyone sees you as offline, but you use the app as usual."
        }
    },
    profile: {
        favorite: 'Add to favorites',
        unfavorite: 'Remove from favorites',
        viewFull: 'View full profile',
        editProfile: 'Edit profile',
        addFriend: 'Add friend',
        cancelRequest: 'Cancel request',
        accept: 'Accept',
        decline: 'Decline',
        removeFriend: 'Remove friend',
        removeConfirm: 'Remove {{name}} from your friends?',
        block: 'Block',
        blockConfirm: 'Block {{name}}? Your friendship ends and you can no longer message each other.',
        unblock: 'Unblock',
        memberSince: 'Member since {{date}}',
        mutualFriendCount: '{{count}} mutual friends',
        mutualGroupCount: '{{count}} mutual groups',
        tabAbout: 'About',
        tabFriends: 'Mutual friends',
        tabGroups: 'Mutual groups',
        noMutualFriends: 'No mutual friends.',
        noMutualGroups: 'No mutual groups.',
        notFound: 'This user could not be found.',
        loadFailed: 'The profile could not be loaded.',
        actionFailed: 'That didn\'t work, try again.'
    },
    profileEdit: {
        title: 'Profile appearance',
        hint: 'The banner, colours, about text and links everyone sees on your profile card.',
        banner: 'Banner',
        uploadBanner: 'Upload banner',
        removeBanner: 'Remove banner',
        bannerColor: 'Banner colour',
        cropBanner: 'Crop banner',
        colors: 'Profile colours',
        colorPrimary: 'Primary colour',
        colorAccent: 'Accent colour',
        colorsHint: 'Your profile card is painted with these two colours.',
        about: 'About me',
        aboutPlaceholder: 'Tell people a little about yourself…',
        aboutFormatting: '**bold**, *italic*, `code` and links work',
        links: 'Links',
        addLink: 'Add link',
        removeLink: 'Remove link',
        platform: 'Platform',
        url: 'Address',
        reset: 'Reset',
        save: 'Save profile',
        saved: 'Profile saved.',
        preview: 'Preview',
        platforms: {
            website: 'Website', github: 'GitHub', steam: 'Steam', spotify: 'Spotify',
            instagram: 'Instagram', youtube: 'YouTube', x: 'X (Twitter)', twitch: 'Twitch'
        },
        errors: {
            TOO_LONG: 'A field is too long.',
            INVALID_COLOR: 'Invalid colour.',
            TOO_MANY: 'You can add at most 5 links.',
            INVALID_URL: 'A link must be a valid address starting with https://.',
            WRONG_SITE: "The link isn't on the selected platform's site.",
            INVALID_PLATFORM: 'Invalid platform.',
            INVALID: 'Invalid value.'
        }
    },
    media: {
        cropTitle: 'Crop photo',
        cropGroupIconTitle: 'Crop group icon',
        zoom: 'Zoom',
        zoomIn: 'Zoom in',
        zoomOut: 'Zoom out',
        apply: 'Save',
        errors: {
            generic: 'The image could not be uploaded.',
            NO_FILE: 'Choose an image.',
            UNSUPPORTED_TYPE: 'This file isn\'t supported. Choose a PNG, JPEG, GIF or WebP.',
            FILE_TOO_LARGE: 'The file is too large - 10 MB at most.',
            TOO_LARGE_DIMENSIONS: 'The image resolution is too high.',
            TOO_MANY_FRAMES: 'The GIF has too many frames (300 at most).',
            TOO_LONG: 'The GIF is too long (15 seconds at most).',
            OUTPUT_TOO_LARGE: 'This GIF is still too large after shrinking it. Try a shorter or smaller one.',
            INVALID_CROP: 'Invalid crop area.',
            BAD_UPLOAD: 'The upload arrived broken, try again.',
            RATE_LIMITED: 'You\'ve uploaded images too often, try again in a bit.',
            NETWORK: 'Connection error, try again.',
            BAD_RESPONSE: 'Unexpected response from the server.'
        }
    },
    navShort: {
        chats: 'Chats',
        friends: 'Friends',
        groups: 'Groups',
        notifications: 'Alerts',
        settings: 'Settings'
    },
    nav: {
        chats: 'Chats',
        friends: 'Friends',
        groups: 'Groups',
        notifications: 'Notifications',
        settings: 'Settings',
        logout: 'Log out',
        admin: 'Admin panel'
    },
    auth: {
        authenticating: 'Authentication is in progress...',
        usernamePlaceholder: 'Username',
        passwordPlaceholder: 'Password',
        confirmPasswordPlaceholder: 'Confirm Password',
        emailPlaceholder: 'Email',
        signIn: 'Sign in',
        signUp: 'Sign Up',
        back: 'Back',
        processing: 'Processing',
        registrationSuccess: 'Your registration is successful. Logging in...',
        forgotPassword: 'Forgot password?',
        forgotPasswordHint: "Enter the email address on your account and we'll send you a link to reset your password.",
        sendResetLink: 'Send reset link',
        forgotPasswordSuccess: "We've sent a link to reset your password. Check your inbox (and spam folder)."
    },
    chat: {
        title: 'Chats',
        subtitle: 'Stay in touch with your friends!',
        emptyFriends: 'Add some friends to start chatting!',
        sayHi: 'Say hi!',
        selectFriend: 'Select a friend to start chatting.',
        inputPlaceholder: 'Send message!',
        today: 'Today',
        yesterday: 'Yesterday',
        sendFailed: 'Message could not be sent.'
    },
    friends: {
        title: 'Friends',
        empty: "You don't have any friends yet. Try searching for someone!",
        accept: 'Accept',
        ignore: 'Ignore',
        unblock: 'Unblock',
        requestSent: 'Request sent to @{{name}}.',
        nowFriends: 'You and @{{name}} are now friends.',
        emptyOnline: 'None of your friends are online right now.',
        emptyPending: 'No pending friend requests.',
        emptyBlocked: "You haven't blocked anyone.",
        incoming: 'Incoming ({{count}})',
        outgoing: 'Sent ({{count}})',
        tabs: { online: 'Online', all: 'All', pending: 'Pending', blocked: 'Blocked' }
    },
    people: {
        subtitle: 'See your friends, find new people.',
        searchPlaceholder: 'Search or add by username',
        clearSearch: 'Clear search',
        friendsSection: 'Friends ({{count}})',
        othersSection: 'Other people',
        typeMore: 'Type at least 3 letters to search other people.',
        searching: 'Searching…',
        searchFailed: "Couldn't search, try again.",
        noResults: 'Nobody matches this search.',
        more: 'Show more',
        incomingTitle: 'Waiting on you ({{count}})',
        suggestionsTitle: 'People you may know',
        mutualFriendsOne: '{{count}} mutual friend',
        mutualFriends: '{{count}} mutual friends',
        mutualGroupsOne: '{{count}} mutual group',
        mutualGroups: '{{count}} mutual groups',
        requestSent: 'Request sent',
        hide: 'Hide',
        welcomeTitle: 'Find your friends',
        welcomeBody: "Nobody here yet. Search for people you know by username or name and add them.",
        findPeople: 'Search people',
        activeTitle: 'Active now ({{count}})',
        inVoice: 'In voice',
        personalRoom: "{{name}}'s room",
        openGroup: 'Go to group',
        favoritesSection: 'Favorites ({{count}})',
        restSection: 'Other friends ({{count}})'
    },
    settings: {
        changePhoto: 'Change photo',
        removePhoto: 'Remove',
        noStatus: "You haven't written a status yet.",
        sectionProfile: 'Profile',
        sectionProfileHint: 'Your name, username and status are visible to everyone.',
        sectionAccount: 'Account',
        sectionAccountHint: 'Your email address is only visible to you.',
        sectionSecurity: 'Security',
        sectionSecurityHint: "We'll ask for your current password before changing it.",
        sectionAppearance: 'Appearance',
        sectionAppearanceHint: 'Language and theme are stored on this device.',
        sectionVoice: 'Voice & calls',
        sectionVoiceHint: 'Choose how you connect to voice rooms.',
        sectionSession: 'Session',
        logoutConfirm: 'Are you sure you want to log out?',
        micDefaultOn: 'Join with microphone on',
        micDefaultOnHint: "When on, your microphone is automatically enabled every time you join a voice room. When off (default), you join muted and turn it on yourself whenever you want.",
        title: 'Settings',
        subtitle: 'Edit your profile settings',
        fullname: 'Fullname',
        username: 'Username',
        status: 'Status',
        email: 'Email Address',
        password: 'Password',
        passwordConfirm: 'Password Confirm',
        currentPassword: 'Current Password',
        discardChanges: 'Discard Changes',
        saveChanges: 'Save Changes',
        loading: 'Loading...',
        language: 'Language',
        theme: 'Theme',
        themeHint: 'The plain themes turn the animated background off.',
        themeNames: {
            'cyclone': 'Cyclone (Default)',
            'plain-blue': 'Plain Blue',
            'dracula': 'Dracula',
            'nord': 'Nord',
            'tokyo-night': 'Tokyo Night',
            'catppuccin': 'Catppuccin Mocha',
            'gruvbox': 'Gruvbox Dark',
            'one-dark': 'One Dark',
            'solarized-light': 'Solarized Light',
            'sakura': 'Sakura Pink',
            'lotr': 'Lord of the Rings',
            'win98': 'Windows 98',
            'macos': 'macOS',
            'valorant': 'Valorant'
        }
    },
    notifications: {
        title: 'Notifications',
        subtitle: 'Friend requests, messages and other activity',
        empty: "You're all caught up. Nothing here yet.",
        justNow: 'just now',
        minutesAgo: '{{count}}m ago',
        hoursAgo: '{{count}}h ago',
        daysAgo: '{{count}}d ago'
    },
    meeting: {
        // Standalone meeting pages (screens/Meeting.js) - the one surface a
        // non-Cyclone guest ever sees, reached from a shared link.
        startMeeting: 'Start a meeting',
        startMeetingDesc: 'Share the link - people without an account can join too',
        creating: 'Creating...',
        activeMeeting: 'You have a meeting running',
        joinYours: 'Join',
        linkCopied: 'Meeting link copied',
        createFailed: 'The meeting could not be created.',
        loading: 'Loading the meeting...',
        info: 'Meeting details',
        shareLink: 'Share this link',
        copyLink: 'Copy link',
        copyInvite: 'Copy invite link',
        copied: 'Copied',
        participants: 'Participants',
        chat: 'Chat',
        chatEmpty: 'No messages yet. Messages here disappear when the meeting ends.',
        chatPlaceholder: 'Send a message to everyone',
        close: 'Close',
        more: 'More options',
        backToGrid: 'Back to grid view',
        endConfirm: 'End the meeting "{{title}}" for everyone?',
        endForAll: 'End the meeting for everyone',
        leave: 'Leave the meeting',
        micOn: 'Turn on the microphone',
        micOff: 'Turn off the microphone',
        cameraOn: 'Turn on the camera',
        cameraOff: 'Turn off the camera',
        share: 'Share your screen',
        stopShare: 'Stop sharing',
        reactions: 'Reactions',
        raiseHand: 'Raise hand',
        lowerHand: 'Lower hand',
        handRaised: 'Hand raised',
        muted: 'Microphone off',
        pin: 'Pin',
        unpin: 'Unpin',
        pinned: 'Pinned',
        fullscreen: 'Fullscreen',
        removeParticipant: 'Remove from the meeting',
        removedNotice: '{{name}} was removed from the meeting.',
        audioDevices: 'Choose a microphone',
        videoDevices: 'Choose a camera',
        microphone: 'Microphone',
        camera: 'Camera',
        screenOf: "{{name}} - screen",
        youSuffix: '(you)',
        waitingForOthers: 'Waiting for others...',
        goToCyclone: 'Go to Cyclone',
        rejoin: 'Rejoin',
        leftTitle: 'You left the meeting',
        leftBody: 'Changed your mind? The same link brings you back.',
        endedTitle: 'The meeting has ended',
        endedBody: 'This link can no longer be used.',
        cyclonePitch: 'Cyclone is an app for groups, voice rooms and chat. Take a look.',
        quality: {
            title: 'Outgoing video',
            high: 'High',
            balanced: 'Balanced',
            low: 'Low',
            hint: 'Affects only what you send. What each person receives is already matched to their tile size automatically.'
        },
        background: {
            title: 'Background',
            none: 'Off',
            blur: 'Blur',
            scenes: { mordor: 'Mordor', space: 'Space' },
            together: 'Put everyone in this scene',
            togetherStop: 'Turn off together mode',
            togetherOn: 'Everyone shares the scene. The tiles have merged into one place.',
            togetherHint: 'Pick a scene first, then you can put everyone in it.'
        },
        annotate: {
            draw: 'Draw on the screen',
            stop: 'Stop drawing',
            clearMine: 'Clear my drawings',
            clearAll: 'Clear all drawings'
        },
        transcript: {
            tab: 'Transcript',
            start: 'Start transcript',
            stop: 'Stop transcript',
            live: 'Transcript on',
            starting: 'Transcript starting...',
            off: 'Transcript off',
            error: 'Transcript error',
            listening: 'Listening - speech will appear here.',
            empty: 'Nothing has been transcribed in this meeting yet.',
            failed: 'Could not change the transcript.',
            summaryTitle: 'Meeting summary',
            summaryPending: 'Preparing the summary...',
            summarySkipped: 'There was not enough speech to summarize.',
            summaryFailed: 'The summary could not be generated; the transcript is still here.'
        },
        lobby: {
            hostedBy: 'Created by {{name}}',
            whoIsInside: 'In the meeting',
            nobodyYet: 'Nobody has joined yet - you will be the first.',
            inside: '{{names}} in the meeting',
            insideWithMore: '{{names}} and {{count}} more in the meeting',
            yourName: 'Your name',
            namePlaceholder: 'Name shown in the meeting',
            joiningAs: 'Joining as {{name}}',
            join: 'Join now',
            joining: 'Joining...',
            hint: 'You can turn your microphone and camera on or off at any time during the meeting.',
            cameraOff: 'Camera off',
            cameraBlocked: 'The camera could not be opened. Check your browser permission.',
            code: 'Meeting code: {{code}}'
        },
        errors: {
            notFoundTitle: 'Meeting not found',
            notFound: 'No meeting exists for this code, or it has expired.',
            joinFailed: 'Could not join the meeting.',
            endFailed: 'Could not end the meeting.',
            removeFailed: 'Could not remove the participant.',
            disconnected: 'You were disconnected from the meeting. You can rejoin.',
            screenPermission: 'Screen sharing permission has not been granted. Allow it in your system settings.'
        }
    },
    groups: {
        changeIcon: 'Change group icon',
        removeIcon: 'Remove group icon',
        title: 'Groups',
        subtitle: 'Hang out with more than one friend at once',
        newGroup: 'New group',
        createGroupTitle: 'Create group',
        noGroupsYet: "You're not in any groups yet.",
        memberCount: '{{count}}/20 members',
        youOwnThis: ' - you own this',
        inviteFriend: 'Invite a friend',
        leaveGroup: 'Leave group',
        leaveGroupConfirm: 'Are you sure you want to leave {{name}}?',
        deleteGroup: 'Delete group',
        deleteGroupConfirm: 'Permanently delete {{name}} with all its members and rooms? This cannot be undone.',
        groupDeleted: 'Group deleted.',
        selectGroup: 'Select a group, or create one.',
        createModalTitle: 'Create a group',
        groupNamePlaceholder: 'Group name',
        create: 'Create',
        inviteModalTitle: 'Invite a friend',
        invite: 'Invite',
        inviteSent: 'Invite sent',
        allFriendsInGroup: 'All your friends are already in this group.',
        addFriendsFirst: 'Add some friends first to invite them here.',
        incomingInvitesTitle: 'Group invites',
        invitedToGroup: '{{inviter}} invited you to join "{{group}}"',
        acceptInvite: 'Accept',
        declineInvite: 'Decline',
        yourSuffix: ' (yours)',
        youSuffix: ' (you)',
        groupOwner: 'Group owner',
        kick: 'Kick',
        kickConfirm: 'Kick {{name}} from the room?',
        manageMembers: 'Manage members',
        membersModalTitle: 'Members',
        removeMember: 'Remove from group',
        removeMemberConfirm: 'Remove {{name}} from the group?',
        noOtherMembers: "You're the only one here so far.",
        roomEmpty: 'Empty',
        enter: 'Enter',
        knock: 'Knock',
        doorState: {
            knock: 'Knock',
            open: 'Open',
            dnd: 'Do Not Disturb',
            locked: 'Locked'
        },
        availability: {
            offline: 'Offline',
            ownerless: 'Empty (owner offline)',
            available: 'Available',
            in_own_room: 'In their room',
            elsewhere: 'Elsewhere'
        }
    },
    knock: {
        knockingOnDoor: '{{name}} is knocking on your door',
        expiresIn: 'Expires in {{seconds}}s',
        letThemIn: 'Let them in',
        ignore: 'Ignore',
        knocking: 'Knocking...',
        waitingForAnswer: 'Waiting for an answer - {{seconds}}s left',
        cancel: 'Cancel',
        resultAccepted: 'They let you in!',
        resultRejected: 'They turned you away.',
        resultExpired: 'Nobody answered in time.',
        resultCancelled: 'Knock cancelled.',
        resultDefault: 'Knock resolved.'
    },
    reactions: {
        title: 'Reactions',
        throw: 'Throw',
        tea: 'Tea for everyone!',
        coffee: 'Coffee for everyone!'
    },
    voiceSheet: {
        expand: 'Open room panel',
        collapse: 'Minimize',
        tabPeople: 'People',
        tabMusic: 'Music',
        tabLyrics: 'Lyrics',
        tabTranscript: 'Transcript',
        mic: 'Mic',
        camera: 'Camera',
        screen: 'Screen',
        video: 'Video',
        transcript: 'Transcript',
        leave: 'Leave',
        speaking: '{{names}} speaking',
        noLyrics: 'Play a song to see its lyrics here.',
        volume: 'Volume'
    },
    voice: {
        inRoom: 'In the room',
        you: 'you',
        participantCount: '{{count}} in room',
        connected: 'Connected',
        connecting: 'Connecting...',
        mute: 'Mute',
        unmute: 'Unmute',
        leaveRoom: 'Leave room',
        defaultRoomLabel: 'Voice room',
        personalRoomLabel: '{{name}}\'s Room',
        microphone: 'Microphone',
        camera: 'Camera',
        cameraOn: 'Turn camera on',
        cameraOff: 'Turn camera off',
        showVideo: 'Show video call',
        hideVideo: 'Hide video call',
        startScreenShare: 'Share your screen',
        stopScreenShare: 'Stop sharing your screen',
        screenSharingTitle: '{{name}} is sharing their screen',
        screenPermissionDenied: 'Cyclone needs Screen Recording permission to share your screen.',
        openSettings: 'Open Settings',
        screenPickerTitle: 'Choose what to share',
        screenPickerScreens: 'Screens',
        screenPickerWindows: 'Windows',
        screenPickerLoading: 'Loading...',
        screenPickerEmpty: 'Nothing to share here.',
        screenPickerMacAudioNote: 'System audio is only included on macOS 15+ via the system picker.'
    },
    music: {
        requestedBy: 'Queued by',
        searchTitle: 'Search & queue',
        queue: 'Up next',
        queueCount: 'Up next ({{count}})',
        addToQueue: 'Add to queue',
        addedToQueue: 'Added to the queue.',
        enqueueFailed: 'Could not add that to the queue.',
        queueFull: 'The queue is full - remove a few tracks first.',
        skip: 'Next track',
        removeFromQueue: 'Remove from queue',
        clearQueue: 'Clear the queue',
        clearQueueConfirm: 'Remove all {{count}} songs from the queue?',
        shuffleQueue: 'Shuffle',
        moveUp: 'Move up',
        moveDown: 'Move down',
        searchPlaceholder: 'Search YouTube...',
        searchButton: 'Search',
        searching: 'Searching...',
        noResults: 'No results found.',
        nowPlaying: 'Now playing',
        play: 'Play',
        pause: 'Pause',
        resume: 'Resume',
        loop: 'Loop',
        loopOn: 'Loop (on)',
        stop: 'Stop',
        error: 'Playback failed - the video may be unavailable.',
        watchTogether: 'Watch together',
        watchTogetherFailed: 'Could not start the watch session.',
        pasteUrlPlaceholder: 'or paste a video link',
        stopWatchTogether: 'Stop watching for everyone',
        stopWatchTogetherConfirm: 'This ends the video for everyone in the room. Are you sure?',
        minimizeWatchTogether: 'Minimize (keeps playing)',
        tapToJoin: 'Tap to join the video',
        showWatchTogether: 'Show video',
        fullscreen: 'Fullscreen',
        exitFullscreen: 'Exit fullscreen',
        invalidVideoUrl: 'That link doesn\'t look like a playable video.',
        youtubeVideoNotFound: 'Could not find that YouTube video.',
        checkingVideoUrl: 'Checking...',
        syncingWithEveryone: 'Syncing with everyone...',
        waitingForParticipant: 'Waiting for {{name}}...',
        pausedForParticipant: 'Paused because of {{name}}\'s connection'
    },
    transcript: {
        start: 'Start transcription',
        startConfirm: 'Everyone in the room will be transcribed and the transcript saved. Start?',
        stop: 'Stop transcription',
        recordingBadge: 'Conversation is being transcribed · started by {{name}}',
        panelTitle: 'Transcript',
        empty: 'No speech recorded yet.',
        summaryReady: 'The conversation summary is ready.',
        historyTitle: 'Past conversations',
        historyLoading: 'Loading...',
        historyEmpty: 'No sessions recorded for this room yet.',
        historySelectPrompt: 'Select a session on the left to see its details.',
        historyLoadFailed: 'Could not load that session.',
        summaryPending: 'Summary is being generated...',
        summaryFailed: 'Could not generate a summary.',
        summarySkipped: 'No summary was generated for this session.',
        summaryRegenerate: 'Regenerate summary',
        summaryRegenerating: 'Generating...',
        summaryRegenerateSuccess: 'Summary updated.',
        summaryRegenerateFailed: 'Could not regenerate the summary, try again.'
    },
    lyrics: {
        panelTitle: 'Lyrics',
        loading: 'Looking up lyrics...',
        notFound: 'No lyrics found for this song.',
        failed: 'Could not load lyrics.',
        expand: 'Fullscreen',
        syncAdjust: 'Sync offset'
    },
    toasts: {
        friendRequestReceived: '{{name}} sent you a friend request',
        friendRequestAccepted: '{{name}} accepted your friend request',
        removedFromRoom: 'You were removed from the room.',
        kickedOtherDevice: 'You joined a room from another device, so this one was disconnected.',
        joinedGroup: '{{name}} joined the group',
        groupInviteReceived: '{{name}} invited you to join {{group}}',
        removedFromGroup: 'The group owner removed you from the group.',
        groupDeleted: '{{name}} was deleted by its owner.'
    },
    admin: {
        nav: {
            dashboard: 'Dashboard',
            users: 'Users',
            settings: 'Settings',
            auditLog: 'Audit log',
            backToApp: 'Back to app'
        },
        dashboard: {
            title: 'Dashboard',
            subtitle: 'Platform overview',
            totalUsers: 'Total users',
            newToday: 'New today',
            newThisWeek: 'New this week',
            newThisMonth: 'New this month',
            online: 'Online now',
            onlineUsersTitle: 'Currently online',
            noOnlineUsers: 'Nobody is online right now.'
        },
        users: {
            title: 'Users',
            subtitle: 'Search, ban, force-logout and manage admin roles',
            searchPlaceholder: 'Search by username, mail or name...',
            filterRoleAll: 'All roles',
            filterRoleAdmin: 'Admin',
            filterRoleUser: 'User',
            filterBannedAll: 'All statuses',
            filterBannedYes: 'Banned',
            filterBannedNo: 'Not banned',
            columnUser: 'User',
            columnRole: 'Role',
            columnStatus: 'Status',
            columnCreated: 'Registered',
            columnLastActive: 'Last active',
            columnActions: 'Actions',
            never: 'Never',
            banned: 'Banned',
            ban: 'Ban',
            unban: 'Unban',
            forceLogout: 'Force logout',
            grantAdmin: 'Grant admin',
            revokeAdmin: 'Revoke admin',
            banReasonLabel: 'Reason (optional)',
            banReasonPlaceholder: 'Why is this account being banned?',
            confirmBan: 'Ban {{username}}? They will be logged out everywhere and unable to log back in.',
            confirmUnban: 'Unban {{username}}? They will be able to log in again.',
            confirmForceLogout: 'Sign {{username}} out on every device?',
            confirmGrantAdmin: 'Grant admin access to {{username}}?',
            confirmRevokeAdmin: "Revoke {{username}}'s admin access?",
            resetProfile: 'Reset profile',
            confirmResetProfile: "Remove the selected parts of {{username}}'s profile?",
            resetParts: { avatar: 'Profile photo', banner: 'Banner and profile colours', text: 'Status line, about me and links' },
            empty: 'No users match this search.',
            prev: 'Previous',
            next: 'Next',
            pageInfo: 'Page {{page}} of {{total}}'
        },
        settings: {
            title: 'Settings',
            subtitle: 'Platform-wide controls',
            registrationOpenLabel: 'New registrations',
            registrationOpenHint: 'When off, new accounts cannot sign up.',
            closedMessageLabel: 'Message shown when registration is closed',
            closedMessagePlaceholder: 'e.g. "Registration is temporarily closed, check back soon."',
            save: 'Save',
            saved: 'Saved.',
            announcementTitle: 'Platform announcement',
            announcementHint: 'Sent instantly to everyone currently online. Not saved anywhere for people who join later.',
            announcementPlaceholder: 'e.g. "Voice servers restart in 10 minutes, you may briefly disconnect."',
            announcementSend: 'Send to everyone online',
            announcementConfirm: 'Send this announcement to every currently connected user?',
            announcementSent: 'Sent.'
        },
        auditLog: {
            title: 'Audit log',
            subtitle: 'Every admin action, who did it and when',
            columnTime: 'Time',
            columnActor: 'Admin',
            columnAction: 'Action',
            columnTarget: 'Target',
            columnDetails: 'Details',
            filterAllActions: 'All actions',
            action: {
                ban: 'Ban',
                unban: 'Unban',
                forceLogout: 'Force logout',
                grantAdmin: 'Grant admin',
                revokeAdmin: 'Revoke admin',
                registrationSettingsChanged: 'Registration settings changed',
                announcement: 'Announcement',
                resetProfile: 'Profile reset'
            },
            empty: 'No admin actions recorded yet.',
            prev: 'Previous',
            next: 'Next'
        }
    },
    errors: {
        generic: 'Something went wrong.',
        notConnected: 'Not connected.',
        NOT_AUTHENTICATED: "Your session isn't authenticated for this action. Try logging in again.",
        ROOM_OFFLINE: "This person isn't around right now.",
        ROOM_LOCKED: 'This door is locked.',
        ROOM_DND: 'This person does not want to be disturbed right now.',
        ROOM_FULL: 'This room is full.',
        NOT_MEMBER: "You're not a member of this group.",
        BANNED: "You've been banned from this room.",
        COOLDOWN: 'You knocked here recently - try again in a bit.',
        ALREADY_KNOCKING: "You're already knocking on this door.",
        RATE_LIMITED: "You're knocking too much - slow down.",
        KNOCK_EXPIRED: 'That knock has expired.',
        KNOCK_ALREADY_RESOLVED: 'That knock was already answered.',
        KNOCK_REQUIRED: 'You need to knock to enter this room.',
        ALREADY_MEMBER: 'This user is already a member of the group.',
        GROUP_FULL: 'This group has reached its member limit (20).',
        ALREADY_INVITED: 'This user already has a pending invite to this group.',
        INVITE_NOT_FOUND: 'That invite no longer exists.',
        NOT_OWNER: 'Only the group owner can remove members.',
        NOT_OWNER_DELETE: 'Only the group owner can delete the group.',
        GROUP_NOT_FOUND: 'Group not found.',
        CANNOT_REMOVE_SELF: 'Use leave instead of removing yourself.'
    }
};

export default en;
